/*
 * Copyright 2026 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext} from "react";
import {ICONS} from "@res/icons";
import {BaseComponent, RoutingModule} from "@intuitionrobotics/thunderstorm/frontend";
import {Route_CheckIn, Route_Default} from "../../Routes";
import {DB_Patient} from "@app/app-shared/patient";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {OrganizationMap, OrganizationsContext} from "./organizations/OrganizationsContext";
import {PatientsContext, PatientsMap} from "./patients/PatientsContext";
import {CheckinModule, OnCheckinUpdated} from "@modules/CheckinModule";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {UserPreferencesModule} from "@modules/UserPreferencesModule";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {useHistory} from "react-router-dom";

const ASSISTED_LIVING_NAME_TOKEN = "masonicare";

export const CheckinPanel: React.FC = () => {
    const {organizations} = useContext(OrganizationsContext);
    return <PatientsContext.Consumer>{
        ({patients}) => <CheckinPanelInner organizations={organizations} patients={patients}/>
    }</PatientsContext.Consumer>;
};

type InnerProps = {
    organizations: OrganizationMap;
    patients: PatientsMap;
};

class CheckinPanelInner extends BaseComponent<InnerProps> implements OnCheckinUpdated {
    __onCheckinUpdated = () => this.forceUpdate();

    componentDidMount() {
        this.refreshFetch();
    }

    componentDidUpdate(prev: InnerProps) {
        if (this.unitIdsKey(prev) !== this.unitIdsKey(this.props))
            this.refreshFetch();
    }

    private targetOrgIds(props: InnerProps): string[] {
        return Object.values(props.organizations)
            .filter((o): o is DB_Organization => !!o && (o.name?.toLowerCase().includes(ASSISTED_LIVING_NAME_TOKEN) ?? false))
            .map(o => o._id);
    }

    private targetPatients(props: InnerProps): DB_Patient[] {
        const orgIds = this.targetOrgIds(props);
        if (!orgIds.length) return [];
        return Object.values(props.patients)
            .filter((p): p is DB_Patient => !!p && orgIds.includes(p.organizationId));
    }

    private unitIdsKey(props: InnerProps): string {
        return this.targetPatients(props).map(p => p.unitId).sort().join(",");
    }

    // Single fetch — the sidebar % doesn't need to be live. The dashboard
    // page polls when visible.
    private refreshFetch = () => {
        const targets = this.targetPatients(this.props);
        if (!targets.length) return;
        CheckinModule.fetchMany(targets.map(p => p.unitId));
    };

    render() {
        const orgIds = this.targetOrgIds(this.props);
        if (!orgIds.length) return null;

        const targets = this.targetPatients(this.props);
        const checkedIn = targets.filter(p => CheckinModule.isCheckedInToday(p.unitId, p.timezone)).length;
        const pct = targets.length ? Math.round((checkedIn / targets.length) * 100) : 0;

        return <CheckinSidebarTile pct={pct}/>;
    }
}

const CheckinSidebarTile: React.FC<{ pct: number }> = ({pct}) => {
    const history = useHistory();
    const selected = (RoutingModule.getMyRouteKey() || Route_Default) === Route_CheckIn;

    const navigate = () => {
        if (selected) return;
        UserPreferencesModule.setLastMainRoute(Route_CheckIn);
        const route = RoutingModule.getRoute(Route_CheckIn);
        history.push({pathname: route.path});
        AnalyticsModule.logCareManagerEvent({
            objectType: Route_CheckIn,
            entity: AnalyticConsts.Entities.USER,
            activityStatus: AnalyticConsts.ActivityStatus.VIEW
        });
    };

    const iconColor = selected ? COLORS.white : COLORS.primary_light_blue;

    return (
        <div
            className={"match_width ll_v_c clickable"}
            style={{
                justifyContent: "center",
                height: 100,
                backgroundColor: selected ? COLORS.primary_blue : COLORS.primary_dark_blue
            }}
            onClick={navigate}
        >
            <div style={{height: 40, marginBottom: 4}}>
                {ICONS.icon_sun_checkin(iconColor, 40)}
            </div>
            <div style={{textAlign: "center", lineHeight: 1.15}}>
                {selected ? FONTS.bold_14_white("Check in") : FONTS.reg_14_lightblue("Check in")}
                {selected ? FONTS.bold_14_white(`(${pct}%)`) : FONTS.reg_14_lightblue(`(${pct}%)`)}
            </div>
        </div>
    );
};
